import { UseQueryResult } from "@tanstack/react-query";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import {
  InternalMessagesByUuids,
  useInternalMessagesByUuids,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useInternalMessages";
import { mapNotNull } from "shared/utils/objectUtils";

export type WithInternalMessages<T> = T & {
  internalMessages: InternalMessageResponse[] | undefined;
};

type BaseEvent = {
  uuid: string;
};

type AggregatedEvent = {
  latestInfoUuid: string;
};

export const withInternalMessages = <T extends BaseEvent>(
  response: T,
  internalMessages: InternalMessageResponse[] | undefined,
): WithInternalMessages<T> => ({ ...response, internalMessages });

const withInternalMessagesByUuids = <T extends BaseEvent>(
  response: T,
  internalMessages: InternalMessagesByUuids,
) => withInternalMessages(response, internalMessages[response.uuid]);

/**
 * Transforms `UseQueryResult<T>[]` to `UseQueryResult<WithInternalMessages<T>>[]`
 */
export const useQueriesWithInternalMessages = <T extends BaseEvent>(
  responses: UseQueryResult<T>[],
) => {
  const uuids = mapNotNull(responses, (it) => it.data?.uuid);
  const internalMessagesByUuids = useInternalMessagesByUuids(uuids);
  return responses.map((it) => ({
    ...it,
    data: it.data
      ? withInternalMessagesByUuids(it.data, internalMessagesByUuids)
      : it.data,
  })) as UseQueryResult<WithInternalMessages<T>>[];
};

/**
 * Transforms `UseQueryResult<T[]>` to `UseQueryResult<WithInternalMessages<T>[]>`
 */
export const useQueryWithInternalMessages = <T extends BaseEvent>(
  responses: UseQueryResult<T[]>,
) => {
  const uuids = responses.data?.map((it) => it.uuid);
  const internalMessagesByUuids = useInternalMessagesByUuids(uuids);
  return {
    ...responses,
    data: responses.data?.map((it) =>
      it ? withInternalMessagesByUuids(it, internalMessagesByUuids) : it,
    ),
  } as UseQueryResult<WithInternalMessages<T>[]>;
};

/**
 * Transforms `UseQueryResult<T[]>` to `UseQueryResult<WithInternalMessages<T>[]>`
 * Note: T is an AggregatedEvent, thus has `latestInfoUuid` instead of `uuid`
 */
export const useQueryAggregatedWithInternalMessages = <
  T extends AggregatedEvent,
>(
  responses: UseQueryResult<T[]>,
) => {
  // Step 1: Field `latestInfoUuid` is renamed to `uuid`
  const responseBaseEvent = {
    ...responses,
    data: responses.data?.map((res) => ({ uuid: res.latestInfoUuid, ...res })),
  } as UseQueryResult<(T & BaseEvent)[]>;

  // Step 2: Now we can use `useQueryWithInternalMessages` as it recognizes `uuid`
  const responsesBaseEventWithInternalMessages =
    useQueryWithInternalMessages(responseBaseEvent);

  // Step 3: Field `uuid` is renamed to `latestInfoUuid`
  const responsesWithInternalMessages = {
    ...responsesBaseEventWithInternalMessages,
    data: responsesBaseEventWithInternalMessages.data?.map(
      ({ uuid: _uuid, ...rest }) => rest,
    ),
  } as UseQueryResult<WithInternalMessages<T>[]>;
  return responsesWithInternalMessages;
};
